import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, CheckCircle, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { SignIn } from '@clerk/clerk-react'
import { DEMO_AUTH_ENABLED } from '@/lib/env'

export default function ForgotPasswordPage() {
  const { signInWithMagicLink } = useAuth()
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)

  if (!DEMO_AUTH_ENABLED) {
    return (
      <div className="min-h-screen bg-gradient-mesh flex items-center justify-center p-4">
        <SignIn
          routing="path"
          path="/forgot-password"
          signUpUrl="/signup"
        />
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email) {
      setError('Please enter your email address')
      return
    }

    try {
      setIsSubmitting(true)
      await signInWithMagicLink(email)
      setSentTo(email)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to send reset link')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-mesh flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-xl border bg-card text-card-foreground p-8 shadow-lg">
        <Link to="/login" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to sign in
        </Link>

        <h1 className="mt-6 text-2xl font-bold text-foreground">Reset your password</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Enter your email and we&apos;ll send a sign-in link you can use to regain access.
        </p>

        {sentTo ? (
          <div className="mt-6 rounded-lg border border-secondary/20 bg-secondary/10 p-4 text-foreground">
            <div className="flex items-start gap-2">
              <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-secondary" />
              <div>
                <p className="font-medium">Email sent</p>
                <p className="mt-1 text-sm">A sign-in link was sent to {sentTo}.</p>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <label htmlFor="email" className="block text-sm font-medium text-foreground">
              Email address
            </label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="w-full rounded-lg border py-3 pl-10 pr-4 outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary"
                placeholder="you@example.com"
                required
              />
            </div>

            {error && (
              <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Sending link...' : 'Send reset link'}
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}
